import { useEffect, useCallback } from "react";

type UpdateStatusFunction = (isOnline: boolean) => void;

const useOnlineStatus = (updateStatus: UpdateStatusFunction, debounceTime: number = 5000): void => {
  const debounce = useCallback((callback: UpdateStatusFunction, delay: number) => {
    let timeoutId: NodeJS.Timeout | null = null;
    return (isOnline: boolean) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => callback(isOnline), delay);
    };
  }, []);

  useEffect(() => {
    const debouncedUpdateStatus = debounce(updateStatus, debounceTime);

    const handleOnline = () => {
      debouncedUpdateStatus(true);
    };

    const handleOffline = () => {
      debouncedUpdateStatus(false);
    };

    // Initial status update
    debouncedUpdateStatus(navigator.onLine);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [updateStatus, debounce, debounceTime]);
};

export default useOnlineStatus;
